import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box, Grid, Card, CardContent, Typography, Chip, CircularProgress,
  Button, Stack, Alert, Divider,
} from '@mui/material'
import { ArrowBack, Devices, Group, Route, Speed } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { useUser } from '../../hooks/useUsers'
import { useUserDeviceSummary } from '../../hooks/useDeviceGroups'

export default function UserDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: user, isLoading } = useUser(id!)
  const { data: summaryData } = useUserDeviceSummary(id!)

  const summary = summaryData?.summary
  const groups = summaryData?.groups ?? []

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  if (!user) return <Alert severity="error">User not found</Alert>

  return (
    <Box>
      <PageHeader
        title={user.name}
        subtitle={user.email}
        breadcrumbs={[{ label: t('users.title'), href: '/users' }, { label: user.name }]}
        action={<Button startIcon={<ArrowBack />} onClick={() => navigate('/users')}>{t('common.back')}</Button>}
      />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Devices sx={{ fontSize: 28, color: '#34A8E0', mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700}>{summary?.device_count ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">{t('nav.devices')}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Group sx={{ fontSize: 28, color: '#34A8E0', mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700}>{summary?.group_count ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">{t('nav.deviceGroups')}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Route sx={{ fontSize: 28, color: '#10b981', mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700}>{summary?.total_trips ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">{t('nav.trips')}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Speed sx={{ fontSize: 28, color: '#f59e0b', mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700}>{(summary?.total_distance_km ?? 0).toFixed(0)}</Typography>
            <Typography variant="caption" color="text.secondary">{t('users.totalKm')}</Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* User Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('users.detail')}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('common.status')}</Typography>
                  <Chip label={user.status ?? 'active'} size="small"
                    color={user.status === 'active' ? 'success' : 'default'} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('users.role')}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {(user.roles ?? []).map((r: string) => (
                      <Chip key={r} label={r} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('device.created')}</Typography>
                  <Typography variant="body2">{new Date(user.created_at).toLocaleDateString()}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Assigned Groups */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('users.assignedGroups')} ({groups.length})</Typography>
              <Divider sx={{ mb: 2 }} />
              {groups.length === 0 ? (
                <Alert severity="info" icon={false}>{t('users.noGroupsAssigned')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {groups.map((g: any) => (
                    <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: g.color }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{g.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{g.device_count} devices</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('users.activity')}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('users.totalTrips')}</Typography>
                  <Typography variant="body2" fontWeight={600}>{summary?.total_trips ?? 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('users.totalDistance')}</Typography>
                  <Typography variant="body2" fontWeight={600}>{(summary?.total_distance_km ?? 0).toFixed(1)} km</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('users.assignedDevices')}</Typography>
                  <Typography variant="body2" fontWeight={600}>{summary?.device_count ?? 0}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  )
}
